// src/components/ui/airport-select.tsx
import * as React from "react"
import { Check, Plane } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase'

type AirportCode = Database['public']['Tables']['airport_codes']['Row']

interface AirportSelectProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}

export function AirportSelect({ value, onChange, placeholder, className }: AirportSelectProps) {
  const [airports, setAirports] = React.useState<AirportCode[]>([])
  const [searchQuery, setSearchQuery] = React.useState("")

  React.useEffect(() => {
    const searchAirports = async () => {
      if (searchQuery.length < 2) {
        setAirports([])
        return
      }

      const { data, error } = await supabase
        .from('airport_codes')
        .select('*')
        .or(
          `city.ilike.%${searchQuery}%,` +
          `country.ilike.%${searchQuery}%,` +
          `code.ilike.%${searchQuery}%`
        )
        .limit(10)

      if (!error && data) {
        setAirports(data)
      }
    }

    const timeoutId = setTimeout(searchAirports, 300)
    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  return (
    <Command className={cn("rounded-lg border shadow-md", className)}>
      <CommandInput
        placeholder={placeholder || "Search airports..."}
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandEmpty>No airports found.</CommandEmpty>
      <CommandGroup className="max-h-[300px] overflow-auto">
        {airports.map((airport) => (
          <CommandItem
            key={airport.code}
            value={airport.code}
            onSelect={() => {
              onChange(airport.code)
              setSearchQuery("")
            }}
          >
            <Check
              className={cn(
                "mr-2 h-4 w-4",
                value === airport.code ? "opacity-100" : "opacity-0"
              )}
            />
            <Plane className="mr-2 h-4 w-4 shrink-0" />
            {`${airport.city}, ${airport.country} (${airport.code})`}
          </CommandItem>
        ))}
      </CommandGroup>
    </Command>
  )
}